/**
 * invitationApi — fetch wrappers for the Hub invitation endpoints (ADR-0010 M5c).
 *
 * IMPORTANT: these calls use the Cognito **ID token**, not the access token.
 * The accept/decline email-match reads `claims.email` (apps/api auth middleware),
 * and the email claim lives ONLY in the ID token — the dashboard hooks
 * (useDashboardKpis etc.) send `accessToken` because they only need
 * `custom:ecosystem_id`. Copying that habit here would make every accept fail
 * `email_mismatch`. See docs/ADR-0010-M5c/DESIGN.md §Token (C1).
 *
 * Endpoints (deployed dev, JWT-authed):
 *   GET  /v1/invitations/me                       → { ok, data: PendingInvitation[] }
 *   POST /v1/invitations/accept  { invitationId, token? }
 *   POST /v1/invitations/decline { invitationId, token? }
 *
 * `token` is optional (ADR-0010 M5c): present for the email-link flow, absent for
 * the in-app inbox (the JWT email-match is the capability there).
 */

export interface PendingInvitation {
  invitationId: string;
  productId: string;
  resourceType: string;
  resourceId: string;
  resourceLabel?: string;
  inviterName?: string;
  role: string;
  status: string;
  expiresAt: number;
  createdAt: number;
}

export type AcceptOutcome =
  | { kind: 'accepted'; productId: string; resourceType: string; resourceId: string; role: string }
  | { kind: 'already'; status: string }
  | { kind: 'not_found' }
  | { kind: 'expired' }
  | { kind: 'invalid_token' }
  | { kind: 'email_mismatch' }
  | { kind: 'bad_request' };

export type DeclineOutcome =
  | { kind: 'declined' }
  | { kind: 'already'; status: string }
  | { kind: 'not_found' }
  | { kind: 'expired' }
  | { kind: 'invalid_token' }
  | { kind: 'email_mismatch' }
  | { kind: 'bad_request' };

/** Thrown only for network / unparseable failures — HTTP error *kinds* are returned, not thrown. */
export class InviteApiFailure extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'InviteApiFailure';
  }
}

interface RawResponse {
  status: number;
  body: { ok?: boolean; data?: unknown; error?: string; status?: string } | null;
}

async function call(
  apiBase: string,
  path: string,
  idToken: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<RawResponse> {
  let res: Response;
  try {
    res = await fetch(`${apiBase.replace(/\/+$/, '')}${path}`, {
      ...init,
      signal,
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new InviteApiFailure('network_error', 'Could not reach the server.');
  }
  let body: RawResponse['body'] = null;
  try {
    body = (await res.json()) as RawResponse['body'];
  } catch {
    body = null;
  }
  return { status: res.status, body };
}

/** Map the {status, error} of an accept/decline response to a discriminated kind. */
function mapActionError(r: RawResponse): Exclude<AcceptOutcome, { kind: 'accepted' }> {
  const error = r.body?.error;
  if (r.status === 404) return { kind: 'not_found' };
  if (r.status === 403) return { kind: 'email_mismatch' };
  if (r.status === 409) return { kind: 'already', status: r.body?.status ?? 'accepted' };
  if (r.status === 410) return error === 'invitation_expired' ? { kind: 'expired' } : { kind: 'invalid_token' };
  return { kind: 'bad_request' };
}

export async function fetchMyInvitations(args: {
  apiBase: string;
  idToken: string;
  signal?: AbortSignal;
}): Promise<PendingInvitation[]> {
  const r = await call(args.apiBase, '/v1/invitations/me', args.idToken, { method: 'GET' }, args.signal);
  if (r.status !== 200 || !r.body?.ok) {
    throw new InviteApiFailure(`http_${r.status}`, 'Could not load your invitations.');
  }
  return (r.body.data as PendingInvitation[]) ?? [];
}

export async function acceptInvitation(args: {
  apiBase: string;
  idToken: string;
  invitationId: string;
  token?: string;
  signal?: AbortSignal;
}): Promise<AcceptOutcome> {
  const payload: Record<string, string> = { invitationId: args.invitationId };
  if (args.token) payload.token = args.token;
  const r = await call(
    args.apiBase,
    '/v1/invitations/accept',
    args.idToken,
    { method: 'POST', body: JSON.stringify(payload) },
    args.signal,
  );
  if (r.status === 200 && r.body?.ok) {
    const d = r.body.data as { productId: string; resourceType: string; resourceId: string; role: string };
    return { kind: 'accepted', productId: d.productId, resourceType: d.resourceType, resourceId: d.resourceId, role: d.role };
  }
  return mapActionError(r);
}

export async function declineInvitation(args: {
  apiBase: string;
  idToken: string;
  invitationId: string;
  token?: string;
  signal?: AbortSignal;
}): Promise<DeclineOutcome> {
  const payload: Record<string, string> = { invitationId: args.invitationId };
  if (args.token) payload.token = args.token;
  const r = await call(
    args.apiBase,
    '/v1/invitations/decline',
    args.idToken,
    { method: 'POST', body: JSON.stringify(payload) },
    args.signal,
  );
  if (r.status === 200 && r.body?.ok) return { kind: 'declined' };
  return mapActionError(r) as DeclineOutcome;
}
